import { useEffect, useCallback, useRef, RefObject } from 'react';

interface UseCursorAttachment {
  setRefs: (refs: { elementRef: RefObject<HTMLElement>; containerRef: RefObject<HTMLElement> }) => void;
  setConditionHandler: (handler: (event: MouseEvent) => boolean) => void;
}

export const useCursorAttachment = (): UseCursorAttachment => {
  const internalElementRef = useRef<RefObject<HTMLElement> | null>(null);
  const internalContainerRef = useRef<RefObject<HTMLElement> | null>(null);
  const lastMousePosition = useRef<{ x: number; y: number } | null>(null);

  // Initialize with a mutable ref to allow reassignment
  const conditionHandler = useRef<(event: MouseEvent) => boolean>(() => true);

  const updatePosition = useCallback((x: number, y: number, event: MouseEvent) => {
    const element = internalElementRef.current?.current;
    const container = internalContainerRef.current?.current;

    if (element && container) {
      const hoveredElement = document.elementFromPoint(x, y) as HTMLElement | null;
      const isChildOfContainer = hoveredElement ? container.contains(hoveredElement) : false;

      // Evaluate the condition if the handler is set
      const meetsCondition = conditionHandler.current ? conditionHandler.current(event) : true;

      if (isChildOfContainer && meetsCondition && lastMousePosition) {
        Object.assign(element.style, {
          position: 'fixed',
          left: `${x}px`,
          top: `${y}px`,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          display: 'block', // Show element when inside container
          cursor: 'move'
        });
      } else {
        element.style.display = 'none'; // Hide element when outside container or condition fails
        document.body.style.cursor = 'default';
      }
    }
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
      updatePosition(event.clientX, event.clientY, event);
    },
    [updatePosition]
  );

  const setRefs = useCallback((refs: { elementRef: RefObject<HTMLElement>; containerRef: RefObject<HTMLElement> }) => {
    internalElementRef.current = refs.elementRef;
    internalContainerRef.current = refs.containerRef;
  }, []);

  const setConditionHandler = useCallback((handler: (event: MouseEvent) => boolean) => {
    conditionHandler.current = handler;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);

    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  return { setRefs, setConditionHandler };
};
